/**
 * Artist Profile Visibility Rules
 * 
 * For imported/admin-curated artists (created_by = null):
 * - Bio: Must be non-empty with meaningful text
 * - Profile Image: Must be a valid image URL
 * - At least one music link: Spotify ID OR Bandcamp URL
 * 
 * For user-created artists (created_by != null):
 * - Must have status = 'approved'
 * - Name, Bio, Genre, and at least one music player required
 * 
 * Verified artists (verified = true) are always public
 * Pending artists are only visible to creator + admins
 */

export interface ArtistProfile {
  bio?: string | null;
  spotify_id?: string | null;
  selected_image?: string | null;
  bandcamp_url?: string | null;
  bandcamp_embed?: string | null;
  soundcloud_embed_url?: string | null;
  // Governance fields
  created_by?: string | null;
  status?: string | null;
  verified?: boolean | null;
  owned_by?: string | null;
  // Basic fields
  name?: string;
  genre?: string[] | null;
}

/**
 * Checks if a string is non-empty and contains meaningful content
 * (not just whitespace or placeholder text)
 */
function isValidBio(bio: string | null | undefined): boolean {
  if (!bio) return false;
  const trimmed = bio.trim();
  // Must have meaningful content (at least 10 characters)
  return trimmed.length >= 10;
}

/**
 * Checks if a Spotify ID is valid
 */
function isValidSpotifyId(spotifyId: string | null | undefined): boolean {
  if (!spotifyId) return false;
  return spotifyId.trim().length > 0;
}

/**
 * Checks if a profile image URL is valid and points to an actual image
 */
function isValidProfileImage(imageUrl: string | null | undefined): boolean {
  if (!imageUrl) return false;
  const trimmed = imageUrl.trim().toLowerCase();
  
  // Accept data URLs (base64 images from fan uploads)
  if (trimmed.startsWith('data:image/')) {
    return true;
  }
  
  // Must start with http/https
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return false;
  }
  
  // Must be an actual image format (not document formats like .djvu, .pdf, etc.)
  const invalidExtensions = ['.djvu', '.pdf', '.doc', '.docx', '.txt', '.xml'];
  const hasInvalidExtension = invalidExtensions.some(ext => trimmed.endsWith(ext));
  if (hasInvalidExtension) {
    return false;
  }
  
  return true;
}

/**
 * Checks if an artist has at least one valid music player
 */
function hasMusicPlayer(artist: ArtistProfile): boolean {
  const hasSpotify = isValidSpotifyId(artist.spotify_id);
  const hasBandcamp = !!(artist.bandcamp_url?.trim()) || !!(artist.bandcamp_embed?.trim());
  const hasSoundcloud = !!(artist.soundcloud_embed_url?.trim());
  return hasSpotify || hasBandcamp || hasSoundcloud;
}

/**
 * Checks if a user-created artist is complete (for approval visibility)
 * Requirements: Name, Bio, Genre, at least one music player
 */
export function isUserCreatedArtistComplete(artist: ArtistProfile): boolean {
  const hasName = !!(artist.name?.trim());
  const hasValidBio = isValidBio(artist.bio);
  const hasGenre = Array.isArray(artist.genre) && artist.genre.length > 0;
  const hasMusic = hasMusicPlayer(artist);
  
  return hasName && hasValidBio && hasGenre && hasMusic;
}

/**
 * Checks if an imported artist is complete (for public visibility)
 * Requirements: Bio, Profile Image, at least one music player
 */
export function isImportedArtistComplete(artist: ArtistProfile): boolean {
  const hasValidBio = isValidBio(artist.bio);
  const hasValidImage = isValidProfileImage(artist.selected_image);
  const hasMusic = hasMusicPlayer(artist);
  
  return hasValidBio && hasValidImage && hasMusic;
}

/**
 * Main visibility check for artist profiles
 * 
 * Logic:
 * 1. Verified artists are always visible
 * 2. User-created artists (created_by != null): visible only if approved AND complete
 * 3. Imported artists (created_by = null): visible only if complete (image, bio, music)
 */
export function isArtistProfileVisible(artist: ArtistProfile): boolean {
  // Verified artists are always public
  if (artist.verified === true) {
    return true;
  }
  
  // User-created artists
  if (artist.created_by) {
    // Must be approved AND complete
    return artist.status === 'approved' && isUserCreatedArtistComplete(artist);
  }
  
  // Imported artists - use completeness rules
  return isImportedArtistComplete(artist);
}

/**
 * Checks if a user can view an artist profile (including pending ones)
 * This is for UI display purposes, not RLS
 */
export function canUserViewArtist(
  artist: ArtistProfile, 
  userId: string | null | undefined,
  isAdmin: boolean
): boolean {
  // Admins can view all
  if (isAdmin) return true;
  
  // Public visibility
  if (isArtistProfileVisible(artist)) return true;
  
  // Creator can view their pending submissions
  if (userId && artist.created_by === userId) return true;
  
  // Owner can view their owned artists
  if (userId && artist.owned_by === userId) return true;
  
  return false;
}

/**
 * Checks if a user can edit an artist profile
 */
export function canUserEditArtist(
  artist: ArtistProfile,
  userId: string | null | undefined,
  isAdmin: boolean
): boolean {
  // Admins can edit all
  if (isAdmin) return true;
  
  if (!userId) return false;
  
  // Creators can edit their pending submissions
  if (artist.created_by === userId && artist.status === 'pending') {
    return true;
  }
  
  // Owners can edit their owned artists
  if (artist.owned_by === userId) {
    return true;
  }
  
  return false;
}

/**
 * Checks if an artist is imported (vs user-created)
 */
export function isImportedArtist(artist: ArtistProfile): boolean {
  return artist.created_by === null || artist.created_by === undefined;
}

/**
 * Filter an array of artists to only include visible profiles
 */
export function filterVisibleArtists<T extends ArtistProfile>(artists: T[]): T[] {
  return artists.filter(isArtistProfileVisible);
}

/**
 * Future: Checks if an indie self-created artist profile is complete
 */
export function isIndieArtistProfileVisible(artist: ArtistProfile): boolean {
  const hasValidBio = isValidBio(artist.bio);
  const hasValidImage = isValidProfileImage(artist.selected_image);
  const hasMusicLink = !!(artist.bandcamp_url?.trim());
  
  return hasValidBio && hasValidImage && hasMusicLink;
}
